/**
 * <ve-reveal> — déclenche les animations d'une section d'accueil VE à son arrivée à l'écran.
 *
 * Amélioration progressive : le contenu n'est masqué qu'une fois ce composant armé
 * (attribut `data-armed`). Sans JavaScript, tout reste visible et lisible.
 *
 * - classe `is-in`  : la section est entrée à l'écran (animation jouée une seule fois) ;
 * - classe `is-off` : la section est hors écran (met en pause les animations en boucle) ;
 * - `data-trail`    : tracé pointillé + avion du bouton vers la carte produit ;
 * - `data-parallax` : image décalée au défilement (ordinateur seulement).
 */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const desktopPointer = window.matchMedia('(min-width: 990px) and (hover: hover)');

class VeReveal extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #enterObserver;
  /** @type {IntersectionObserver | undefined} */
  #visibilityObserver;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {number} */
  #frame = 0;
  #visible = false;
  #onScroll = () => this.#requestParallax();

  connectedCallback() {
    const designMode = Boolean(/** @type {any} */ (window).Shopify?.designMode);

    if (reduceMotion.matches || designMode) {
      this.classList.add('is-in');
      return;
    }

    this.setAttribute('data-armed', '');

    this.#enterObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.classList.add('is-in');
        this.#enterObserver?.disconnect();
        if (this.hasAttribute('data-trail')) this.#flyTrail();
      },
      { threshold: 0.3 }
    );
    this.#enterObserver.observe(this);

    this.#visibilityObserver = new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting);
      this.classList.toggle('is-off', !this.#visible);
    });
    this.#visibilityObserver.observe(this);

    if (this.querySelector('[data-parallax]')) {
      window.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#requestParallax();
    }

    if (this.hasAttribute('data-trail')) {
      this.#resizeObserver = new ResizeObserver(() => {
        if (this.classList.contains('is-in')) this.#drawTrail(true);
      });
      this.#resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.#enterObserver?.disconnect();
    this.#visibilityObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    window.removeEventListener('scroll', this.#onScroll);
    cancelAnimationFrame(this.#frame);
  }

  /* ---------- Parallaxe : un calcul par image au plus, jamais sur mobile ---------- */

  #requestParallax() {
    if (this.#frame || !this.#visible || !desktopPointer.matches) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      const target = /** @type {HTMLElement | null} */ (this.querySelector('[data-parallax]'));
      if (!target) return;
      const rect = this.getBoundingClientRect();
      const offset = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
      target.style.setProperty('--ve-parallax', `${(offset * -28).toFixed(1)}px`);
    });
  }

  /* ---------- Tracé de l'avion ---------- */

  /**
   * Calcule le tracé entre le bouton et la carte, en pixels de la zone de contenu.
   * @param {boolean} complete - afficher le tracé entier (après l'animation ou au redimensionnement)
   * @returns {SVGPathElement | null}
   */
  #drawTrail(complete = false) {
    const box = /** @type {HTMLElement | null} */ (this.querySelector('[data-trail-box]'));
    const from = this.querySelector('[data-trail-from]');
    const to = this.querySelector('[data-trail-to]');
    const svg = this.querySelector('[data-trail-svg]');
    const dash = /** @type {SVGPathElement | null} */ (this.querySelector('[data-trail-dash]'));
    const mask = /** @type {SVGPathElement | null} */ (this.querySelector('[data-trail-mask]'));
    if (!box || !from || !to || !svg || !dash || !mask) return null;

    // Positions de mise en page, hors transformations : la carte et le texte sont
    // encore en mouvement (roulement, glissement) au moment de la mesure.
    const area = { width: box.offsetWidth, height: box.offsetHeight };
    const start = layoutRect(/** @type {HTMLElement} */ (from), box);
    const end = layoutRect(/** @type {HTMLElement} */ (to), box);
    const stacked = end.top > start.bottom;
    const rtl = end.right < start.left;

    const a = {
      x: rtl ? start.left - 18 : start.right + 18,
      y: start.top + start.height / 2,
    };
    // empilé (mobile) : atterrissage au-dessus du coin haut droit de la carte, sans sortir de l'écran
    const b = stacked
      ? { x: end.right - 36, y: end.top - 16 }
      : { x: rtl ? end.right + 18 : end.left - 18, y: end.top + end.height * 0.35 };
    const bend = rtl ? -1 : 1;
    const d = stacked
      ? `M${a.x},${a.y} C${a.x + 50},${a.y} ${b.x + 12},${b.y - 50} ${b.x},${b.y}`
      : `M${a.x},${a.y} C${a.x + 120 * bend},${a.y - 40} ${b.x - 160 * bend},${b.y - 220} ${b.x},${b.y}`;

    svg.setAttribute('viewBox', `0 0 ${area.width} ${area.height}`);
    dash.setAttribute('d', d);
    mask.setAttribute('d', d);

    if (complete) {
      mask.style.strokeDasharray = 'none';
      mask.style.strokeDashoffset = '0';
    }
    return dash;
  }

  #flyTrail() {
    const path = this.#drawTrail();
    const mask = /** @type {SVGPathElement | null} */ (this.querySelector('[data-trail-mask]'));
    const plane = /** @type {HTMLElement | null} */ (this.querySelector('[data-trail-plane]'));
    if (!path || !mask || !plane) return;

    const length = path.getTotalLength();
    mask.style.strokeDasharray = `${length}`;
    mask.style.strokeDashoffset = `${length}`;

    const delay = 700;
    const duration = 1900;
    const origin = performance.now() + delay;
    plane.classList.add('is-flying');

    /** @param {number} now */
    const step = (now) => {
      const progress = Math.min(Math.max((now - origin) / duration, 0), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const distance = eased * length;
      const point = path.getPointAtLength(distance);
      const ahead = path.getPointAtLength(Math.min(distance + 1, length));
      const angle = (Math.atan2(ahead.y - point.y, ahead.x - point.x) * 180) / Math.PI;

      // transform uniquement : pas de recalcul de mise en page à chaque image
      plane.style.transform = `translate(${point.x}px, ${point.y}px) rotate(${angle}deg)`;
      mask.style.strokeDashoffset = `${length - distance}`;

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        window.setTimeout(() => plane.classList.remove('is-flying'), 500);
      }
    };
    requestAnimationFrame(step);
  }
}

/**
 * Rectangle d'un élément relatif à un ancêtre, calculé sur les positions de mise en
 * page (offset*) : ignore les transform en cours d'animation.
 * @param {HTMLElement} element
 * @param {HTMLElement} ancestor
 */
function layoutRect(element, ancestor) {
  let left = 0;
  let top = 0;
  /** @type {Element | null} */
  let node = element;
  while (node instanceof HTMLElement && node !== ancestor) {
    left += node.offsetLeft;
    top += node.offsetTop;
    node = node.offsetParent;
  }
  const width = element.offsetWidth;
  const height = element.offsetHeight;
  return { left, top, width, height, right: left + width, bottom: top + height };
}

if (!customElements.get('ve-reveal')) {
  customElements.define('ve-reveal', VeReveal);
}
